import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import "./Dashboard.css";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Package, Clock, CheckCircle, Plus, TrendingUp, MessageCircle, Calculator, BookOpen, AlertTriangle, Bell, DollarSign, Users, Calendar, FileText, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TrialBannerSmall } from "@/components/TrialBannerSmall";
import { PaymentExpirationWarning } from "@/components/PaymentExpirationWarning";
import { useQuery } from "@tanstack/react-query";
import { listOrders } from "@/integrations/supabase/orders";
import { listQuotes } from "@/integrations/supabase/quotes";
import { listReceitas } from "@/integrations/supabase/receitas";
import { listInventory } from "@/integrations/supabase/inventory";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";
import { useInternationalization } from "@/contexts/InternationalizationContext";
import { startStockAlerts, checkStockNow } from "@/utils/stockAlerts";
import { LoadingCard, SkeletonCard } from "@/components/ui/loading";
import { PageTransition, StaggeredAnimation, FadeIn } from "@/components/ui/page-transition";
import { MobileCard, MobileGrid } from "@/components/ui/mobile-form";
import { DashboardControls, useDashboardControls } from "@/components/DashboardControls";
import { supabase } from "@/integrations/supabase/client";
import { isUpdateCardHidden, hideUpdateCard, showUpdateCard } from "@/integrations/supabase/user-preferences";
import { getCurrentEmpresaId } from "@/integrations/supabase/auth-utils";
import { saveFinancialValuesPreference, getFinancialValuesPreference, saveUpdateCardExpandedPreference, getUpdateCardExpandedPreference } from "@/integrations/supabase/user-prefs"; // Salva preferência no Supabase
import { calcularSaldoRealAtualAsync } from "@/utils/financialUtils";
import React, { useMemo, lazy, Suspense, useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { parseISODateAsLocal, formatDateBR } from "@/utils/dateOnly";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSyncCustomers } from "@/hooks/useSyncCustomers";

// Lazy loading de componentes pesados para melhorar performance
const ChatWidget = lazy(() => import("@/components/ChatWidget").then(m => ({ default: m.ChatWidget })));
const OnboardingChecklist = lazy(() => import("@/components/OnboardingChecklist").then(m => ({ default: m.OnboardingChecklist })));
const ValueDashboard = lazy(() => import("@/components/ValueDashboard").then(m => ({ default: m.ValueDashboard })));
const AchievementsBadges = lazy(() => import("@/components/AchievementsBadges").then(m => ({ default: m.AchievementsBadges })));
const ReferralProgram = lazy(() => import("@/components/ReferralProgram").then(m => ({ default: m.ReferralProgram })));
const InAppMessages = lazy(() => import("@/components/InAppMessages").then(m => ({ default: m.InAppMessages })));

export default function Dashboard() {
  const navigate = useNavigate();
  const { empresa, user } = useAuth();
  const { formatCurrency } = useInternationalization();
  const [hoverCard, setHoverCard] = useState<string | null>(null);
  const { engagementVisible, compactMode, setEngagementVisible, setCompactMode } = useDashboardControls();

  // 🔄 Sincronizar clientes de pedidos antigos (executa apenas 1 vez)
  useSyncCustomers();
  const [updateModalOpen, setUpdateModalOpen] = React.useState(false);
  const updatesKey = "ateliepro_updates_2026_05_19_categorias_v3";
  const [dontShowAgain, setDontShowAgain] = React.useState(false);
  const [checkingUpdates, setCheckingUpdates] = React.useState(true);

  // Estado para controlar visibilidade do card de atualizações
  const [showUpdateCard, setShowUpdateCard] = useState(true);
  const [isLoadingCardPref, setIsLoadingCardPref] = useState(true);
  const [isExpandedUpdateCard, setIsExpandedUpdateCard] = useState(true);

  // Estado para ofuscar valores financeiros
  const [showFinancialValues, setShowFinancialValues] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const isInitialMount = React.useRef(true);

  // Carregar preferência do Supabase ao montar
  useEffect(() => {
    const loadPreference = async () => {
      console.log('🔄 Dashboard: Carregando preferência de valores financeiros...');
      const saved = await getFinancialValuesPreference();
      console.log('🔄 Dashboard: Preferência carregada ->', saved);
      setShowFinancialValues(saved);
      isInitialMount.current = false;
    };
    loadPreference();
  }, [user?.id]);

  // Salvar preferência no Supabase quando mudar (mas não na montagem inicial)
  useEffect(() => {
    if (user?.id && !isInitialMount.current) {
      console.log('🔄 Dashboard: Salvando preferência de valores financeiros ->', showFinancialValues);
      saveFinancialValuesPreference(showFinancialValues);
    }
  }, [showFinancialValues, user?.id]);

  // Verificar preferência de visibilidade do card no banco de dados
  useEffect(() => {
    const checkCardVisibility = async () => {
      if (!user?.id) {
        setIsLoadingCardPref(false);
        return;
      }

      try {
        const isHidden = await isUpdateCardHidden(user.id);
        setShowUpdateCard(!isHidden);
      } catch (error) {
        console.error('Erro ao verificar visibilidade do card:', error);
        setShowUpdateCard(true); // Mostrar por padrão em caso de erro
      } finally {
        setIsLoadingCardPref(false);
      }
    };

    checkCardVisibility();
  }, [user?.id]);

  // Carregar preferência do card expandido ao montar
  useEffect(() => {
    const loadPreference = async () => {
      console.log('🔄 Dashboard: Carregando preferência de card expandido...');
      const saved = await getUpdateCardExpandedPreference();
      console.log('🔄 Dashboard: Preferência de card expandido carregada ->', saved);
      setIsExpandedUpdateCard(saved);
      isInitialMount.current = false;
    };
    if (isInitialMount.current) {
      loadPreference();
    }
  }, [user?.id]);

  // Salvar preferência do card expandido quando mudar (mas não na montagem inicial)
  useEffect(() => {
    if (user?.id && !isInitialMount.current) {
      console.log('🔄 Dashboard: Salvando preferência de card expandido ->', isExpandedUpdateCard);
      saveUpdateCardExpandedPreference(isExpandedUpdateCard);
    }
  }, [isExpandedUpdateCard, user?.id]);

  // Função para recolher/expandir o card
  const handleToggleExpandUpdateCard = () => {
    setIsExpandedUpdateCard(!isExpandedUpdateCard);
  };

  const saveUpdatePreference = React.useCallback(async () => {
    if (!dontShowAgain || !user?.id) return;

    try {
      const { error } = await supabase
        .from("user_update_preferences")
        .upsert(
          {
            user_id: user.id,
            updates_version: updatesKey,
            dismissed: true,
            dismissed_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );

      if (error) {
        console.warn("Erro ao salvar preferência de atualização:", error);
        return;
      }

      window.localStorage.setItem(updatesKey, "seen");
    } catch (err) {
      console.warn("Falha ao salvar preferência de atualização:", err);
    }
  }, [dontShowAgain, updatesKey, user?.id]);
  // Iniciar sistema de alertas de estoque
  React.useEffect(() => {
    startStockAlerts();
    return () => {
      // Cleanup sera feito pelo proprio sistema
    };
  }, []);

  React.useEffect(() => {
    const loadUpdatePreference = async () => {
      if (!user?.id) {
        setCheckingUpdates(false);
        return;
      }

      try {
        const localSeen = window.localStorage.getItem(updatesKey) === "seen";
        if (localSeen) {
          setUpdateModalOpen(false);
          return;
        }

        const { data, error } = await supabase
          .from("user_update_preferences")
          .select("updates_version, dismissed")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          console.warn("Erro ao carregar preferências de atualização:", error);
          setUpdateModalOpen(true);
          return;
        }

        if (!data || !data.dismissed || data.updates_version !== updatesKey) {
          setUpdateModalOpen(true);
          return;
        }

        setUpdateModalOpen(false);
      } catch (err) {
        console.warn("Falha ao verificar preferências de atualização:", err);
        setUpdateModalOpen(true);
      } finally {
        setCheckingUpdates(false);
      }
    };

    loadUpdatePreference();
  }, [user?.id]);
  
  // Buscar dados com prioridade - carregar críticos primeiro, secundários depois
  // Prioridade 1: Orders (mais importante para o dashboard)
  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: listOrders,
    staleTime: 5000, // Cache por apenas 5 segundos (era 30000)
    gcTime: 1 * 60 * 1000, // Manter cache por 1 minuto (era 2 minutos)
    refetchInterval: 10000, // Refetch a cada 10 segundos (auto-refresh)
  });

  // Prioridade 2: Quotes (importante mas pode esperar um pouco)
  const { data: quotes = [], isLoading: quotesLoading } = useQuery({
    queryKey: ["quotes"],
    queryFn: listQuotes,
    staleTime: 30000,
    gcTime: 2 * 60 * 1000,
    // Carregar apenas se orders já carregou (para não bloquear)
    enabled: !ordersLoading,
  });

  // Prioridade 3: Receitas (pode carregar depois)
  const { data: receitas = [], isLoading: receitasLoading } = useQuery({
    queryKey: ["receitas"],
    queryFn: listReceitas,
    staleTime: 30000,
    gcTime: 2 * 60 * 1000,
    // Carregar apenas se orders já carregou
    enabled: !ordersLoading,
  });

  // Prioridade 4: Inventory (menos crítico, carregar por último)
  const { data: inventory = [], isLoading: inventoryLoading } = useQuery({
    queryKey: ["inventory"],
    queryFn: listInventory,
    staleTime: 60000, // Cache por 1 minuto (inventory muda menos)
    gcTime: 5 * 60 * 1000, // Manter cache por 5 minutos
    // Carregar apenas se orders já carregou
    enabled: !ordersLoading,
  });

  // Buscar dados de Saúde Financeira
  const { data: saldoAtual = null, isLoading: saldoLoading } = useQuery({
    queryKey: ["dashboard-saldo-atual"],
    queryFn: calcularSaldoRealAtualAsync,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Buscar contas a receber para calcular vencimentos
  const { data: contasReceber = [] } = useQuery({
    queryKey: ["dashboard-contas-receber"],
    queryFn: async () => {
      try {
        const empresaId = await getCurrentEmpresaId();
        if (!empresaId) return [];

        const { data, error } = await supabase
          .from('contas_receber')
          .select('valor_total, valor_recebido, data_vencimento, status')
          .eq('empresa_id', empresaId)
          .neq('status', 'cancelado');

        return error ? [] : (data || []);
      } catch {
        return [];
      }
    },
    staleTime: 30000,
  });

  // Buscar contas a pagar para calcular despesas próximas
  const { data: contasPagar = [] } = useQuery({
    queryKey: ["dashboard-contas-pagar"],
    queryFn: async () => {
      try {
        const empresaId = await getCurrentEmpresaId();
        if (!empresaId) return [];

        const { data, error } = await supabase
          .from('contas_pagar')
          .select('valor_total, valor_pago, data_vencimento, status')
          .eq('empresa_id', empresaId)
          .neq('status', 'cancelado');

        return error ? [] : (data || []);
      } catch {
        return [];
      }
    },
    staleTime: 30000,
  });

  // Buscar template WhatsApp personalizado
  const { data: whatsappTemplate } = useQuery({
    queryKey: ["whatsapp-templates", empresa?.id, "dashboard_intro"],
    queryFn: async () => {
      if (!empresa?.id) return null;

      const { getWhatsAppTemplate, processTemplate, getWhatsAppSettings, addSignature } = await import("@/utils/whatsappTemplates");
      const template = await getWhatsAppTemplate(empresa.id, 'dashboard_intro');
      
      if (template) {
        const processed = processTemplate(template, {}, empresa);
        const settings = await getWhatsAppSettings(empresa.id);
        return { message_text: addSignature(processed, settings) };
      }

      return null;
    },
    enabled: !!empresa?.id,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  // Mostrar loading apenas se orders ainda estiver carregando (conteúdo crítico)
  const isCriticalLoading = ordersLoading;

  // Funcao para enviar WhatsApp (memoizada)
  const sendWhatsApp = React.useCallback((message: string) => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success("Mensagem do WhatsApp preparada!");
  }, []);

  // Calcular métricas de Saúde Financeira
  const healthMetrics = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + 30);

    // Contas a receber nos próximos 30 dias
    const aReceber = contasReceber
      .filter(c => c.status === 'pendente' || c.status === 'atrasado')
      .reduce((sum, c) => sum + ((c.valor_total || 0) - (c.valor_recebido || 0)), 0);

    // Contas a pagar nos próximos 30 dias
    const aPagar = contasPagar
      .filter(c => c.status === 'pendente' || c.status === 'atrasado')
      .reduce((sum, c) => sum + ((c.valor_total || 0) - (c.valor_pago || 0)), 0);

    // Contas atrasadas (vencimento < hoje)
    const atrasados = contasReceber
      .filter(c => {
        const venc = new Date(c.data_vencimento);
        return (c.status === 'pendente' || c.status === 'atrasado') && venc < today;
      })
      .reduce((sum, c) => sum + ((c.valor_total || 0) - (c.valor_recebido || 0)), 0);

    // Próximo vencimento
    const proximosVencimentos = [...contasReceber, ...contasPagar]
      .filter(c => {
        const venc = new Date(c.data_vencimento);
        return venc >= today && venc <= futureDate && (c.status === 'pendente' || c.status === 'atrasado');
      })
      .sort((a, b) => new Date(a.data_vencimento).getTime() - new Date(b.data_vencimento).getTime());

    const proximoVencimento = proximosVencimentos[0]?.data_vencimento || null;

    // Status de saúde
    const saldoNum = saldoAtual?.saldoAtual || 0;
    const saudavel = aReceber > aPagar && atrasados === 0 && saldoNum > 0;

    return {
      saldo: saldoNum,
      aReceber,
      aPagar,
      atrasados,
      proximoVencimento,
      saudavel,
    };
  }, [contasReceber, contasPagar, saldoAtual]);

  // Memoizar cálculos pesados para evitar recálculos desnecessários
  const ordersStats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const activeOrders = orders.filter(o => o.status !== "Cancelado");
    
    // 🔧 FIX: "Em Andamento" = apenas pedidos EM PRODUÇÃO (não prontos, não aguardando, não entregues)
    const inProgressOrders = activeOrders.filter(o => 
      o.status === "Em produção"
    );
    
    const inProduction = activeOrders.filter(o => o.status === "Em produção");
    const ready = activeOrders.filter(o => o.status === "Pronto");
    const awaitingPickup = activeOrders.filter(o => o.status === "Aguardando retirada");
    
    // 🔧 FIX: Contar pedidos entregues OU prontos HOJE
    const completedToday = orders.filter(order => {
      if (!["Entregue", "Pronto"].includes(order.status)) return false;
      
      // Verificar por delivery_date ou updated_at
      let checkDate = null;
      
      if (order.delivery_date) {
        checkDate = parseISODateAsLocal(order.delivery_date);
      } else if (order.updated_at) {
        checkDate = parseISODateAsLocal(order.updated_at);
      }
      
      if (!checkDate) return false;
      checkDate.setHours(0, 0, 0, 0);
      return checkDate.getTime() === today.getTime();
    });
    
    const overdueOrders = activeOrders.filter(order => {
      if (!order.delivery_date) return false;
      const deliveryDate = parseISODateAsLocal(order.delivery_date);
      deliveryDate.setHours(0, 0, 0, 0);
      return deliveryDate < today && order.status !== "Entregue";
    });

    const urgentOrders = activeOrders.filter(order => {
      if (!order.delivery_date) return false;
      const deliveryDate = parseISODateAsLocal(order.delivery_date);
      deliveryDate.setHours(0, 0, 0, 0);
      const diffTime = deliveryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 3 && diffDays >= 0 && order.status !== "Entregue";
    });

    const pendingPayments = activeOrders.filter(order => {
      const totalValue = Number(order.value || 0);
      const paidAmount = Number(order.paid || 0);
      return totalValue > paidAmount;
    });

    // 🔧 FIX: Receita do mês = soma de pedidos ATIVOS (não cancelados)
    const totalRevenue = activeOrders.reduce((sum, order) => 
      sum + (Number(order.value) || 0), 0
    );
    
    // 🔧 NEW: Receita recebida = soma de pedidos PAGOS (entregues ou não)
    const receivedRevenue = orders
      .filter(order => {
        const totalValue = Number(order.value || 0);
        const paidAmount = Number(order.paid || 0);
        return totalValue > 0 && paidAmount > 0; // Qualquer valor recebido
      })
      .reduce((sum, order) => sum + (Number(order.paid) || 0), 0); // ✅ Usar order.paid, não order.value

    return {
      total: inProgressOrders.length, // 🔧 Agora = pedidos EM ANDAMENTO (não finalizados)
      inProduction: inProduction.length,
      ready: completedToday.length, // 🔧 Agora = "Entregue" + "Pronto" HOJE
      awaitingPickup: awaitingPickup.length,
      overdue: overdueOrders,
      urgent: urgentOrders,
      pendingPayments: pendingPayments.slice(0, 3),
      totalRevenue, // 🔧 Pedidos ATIVOS
      receivedRevenue // 🔧 Pedidos PAGOS
    };
  }, [orders]);

  // Memoizar receitas total (apenas receitas com status 'pago')
  const receitasTotal = useMemo(() => {
    return receitas
      .filter(receita => receita.status === 'pago' || !receita.status) // Considerar receitas sem status como pagas (compatibilidade com dados antigos)
      .reduce((sum, receita) => sum + (Number(receita.amount) || 0), 0);
  }, [receitas]);

  // Memoizar estoque crítico
  const criticalItems = useMemo(() => {
    return inventoryLoading ? [] : inventory.filter(item => item.status === "critical").slice(0, 2);
  }, [inventory, inventoryLoading]);

  // Memoizar orçamentos pendentes
  const pendingQuotes = useMemo(() => {
    return quotesLoading ? [] : quotes.filter((quote: any) => quote.status === "pending").slice(0, 2);
  }, [quotes, quotesLoading]);

  // Processar alertas inteligentes (memoizado)
  const intelligentAlerts = useMemo(() => {
    const alerts = [];

    // 1. Pedidos atrasados
    ordersStats.overdue.forEach(order => {
      alerts.push({
        id: `overdue-${order.id}`,
        type: "overdue",
        priority: "high",
        title: "Pedido Atrasado",
        message: `${order.code} - ${order.customer_name}`,
        icon: AlertTriangle,
        color: "red",
        action: () => {
          const message = `Olá ${order.customer_name}!

Lembramos que seu pedido ${order.code} estava previsto para entrega em ${formatDateBR(order.delivery_date)}.

*DETALHES:*
• Tipo: ${order.type}
• Status: ${order.status}
• Valor: R$ ${order.value?.toFixed(2) || '0,00'}

Por favor, entre em contato para agendarmos a entrega.

_${empresa?.nome || 'Atelie'}_`;
          sendWhatsApp(message);
        }
      });
    });

    // 2. Pagamentos pendentes
    ordersStats.pendingPayments.forEach(order => {
      const totalValue = Number(order.value || 0);
      const paidAmount = Number(order.paid || 0);
      const remaining = totalValue - paidAmount;
      
      alerts.push({
        id: `payment-${order.id}`,
        type: "payment",
        priority: "medium",
        title: "Pagamento Pendente",
        message: `${order.code} - R$ ${remaining.toFixed(2)} restante`,
        icon: DollarSign,
        color: "orange",
        action: () => {
          const message = `Olá ${order.customer_name}!

Lembramos sobre o pagamento do pedido ${order.code}.

*VALORES:*
• Total: R$ ${totalValue.toFixed(2)}
• Pago: R$ ${paidAmount.toFixed(2)}
• Restante: R$ ${remaining.toFixed(2)}

Por favor, entre em contato para quitar o saldo.

_${empresa?.nome || 'Atelie'}_`;
          sendWhatsApp(message);
        }
      });
    });

    // 3. Estoque crítico
    criticalItems.forEach(item => {
      alerts.push({
        id: `stock-${item.id}`,
        type: "stock",
        priority: "high",
        title: "Estoque Critico",
        message: `${item.name} - ${item.quantity} ${item.unit}`,
        icon: Package,
        color: "red",
        action: () => {
          const message = `*ALERTA DE ESTOQUE*

Item: ${item.name}
Quantidade atual: ${item.quantity} ${item.unit}
Status: CRITICO

E necessario repor urgentemente este item!`;
          sendWhatsApp(message);
        }
      });
    });

    // 4. Orçamentos aguardando aprovação
    pendingQuotes.forEach((quote: any) => {
      alerts.push({
        id: `quote-${quote.id}`,
        type: "quote",
        priority: "medium",
        title: "Orcamento Pendente",
        message: `${quote.code} - ${quote.customer_name}`,
        icon: FileText,
        color: "blue",
        action: () => {
          const message = `Olá ${quote.customer_name}!

Seu orcamento ${quote.code} esta pronto e aguardando aprovacao.

*VALOR TOTAL: R$ ${quote.total_value?.toFixed(2) || '0,00'}*

Por favor, confirme se esta de acordo ou entre em contato para ajustes.

_${empresa?.nome || 'Atelie'}_`;
          sendWhatsApp(message);
        }
      });
    });

    // 5. Pedidos próximos do prazo
    ordersStats.urgent.forEach(order => {
      const deliveryDate = parseISODateAsLocal(order.delivery_date);
      const today = new Date();
      const diffTime = deliveryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      alerts.push({
        id: `urgent-${order.id}`,
        type: "urgent",
        priority: "high",
        title: "Prazo Proximo",
        message: `${order.code} - ${diffDays} dias restantes`,
        icon: Clock,
        color: "orange",
        action: () => {
          const message = `Olá ${order.customer_name}!

Seu pedido ${order.code} tem entrega prevista para ${deliveryDate.toLocaleDateString('pt-BR')} (${diffDays} dias).

*STATUS ATUAL: ${order.status}*

Em caso de duvidas sobre o andamento, entre em contato conosco!

_${empresa?.nome || 'Atelie'}_`;
          sendWhatsApp(message);
        }
      });
    });

    return alerts.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }, [ordersStats, criticalItems, pendingQuotes, empresa?.nome, sendWhatsApp]);

  return (
    <PageTransition className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-slate-50">
      {/* Chat Widget para Suporte Proativo - Lazy loaded */}
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>

      {/* Modal desativado - Novidades da versão */}
      <Dialog
        open={false}
        onOpenChange={() => {}}
      >
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">✨ Novidades da nova versão</DialogTitle>
            <DialogDescription>
              Confira o que chegou de novo no Ateliê Pro para você aproveitar hoje.
            </DialogDescription>
          </DialogHeader>
          
          {/* DESTAQUE PRINCIPAL - CATEGORIAS CUSTOMIZÁVEIS */}
          <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50 shadow-sm">
            <p className="font-bold text-green-900 text-lg">🎯 Categorias customizáveis</p>
            <p className="text-green-800 mt-1">Crie suas próprias categorias de produtos! 8 pré-definidas + ilimitadas customizadas. Controle total do seu catálogo.</p>
          </div>

          {/* OUTRAS NOVIDADES EM ACCORDION */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="other-updates">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline">
                📋 Outras melhorias e novidades
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pt-4">
                <div className="p-3 rounded-lg border border-purple-100 bg-purple-50/50">
                  <p className="font-semibold text-purple-900">📄 Orçamentos com identidade visual</p>
                  <p className="text-purple-800">Logo e dados da empresa agora aparecem nos PDFs automaticamente.</p>
                </div>
                <div className="p-3 rounded-lg border border-blue-100 bg-blue-50/50">
                  <p className="font-semibold text-blue-900">🖨️ Impressão padronizada</p>
                  <p className="text-blue-800">O botão Imprimir sempre usa o PDF oficial, com informações completas.</p>
                </div>
                <div className="p-3 rounded-lg border border-emerald-100 bg-emerald-50/50">
                  <p className="font-semibold text-emerald-900">⚡ Fluxo mais rápido</p>
                  <p className="text-emerald-800">Novos ajustes de usabilidade deixam as telas mais ágeis.</p>
                </div>
                <div className="p-3 rounded-lg border border-amber-100 bg-amber-50/50">
                  <p className="font-semibold text-amber-900">🧵 PDV integrado</p>
                  <p className="text-amber-800">Novo PDV para vendas rápidas direto no Ateliê Pro.</p>
                </div>
                <div className="p-3 rounded-lg border border-rose-100 bg-rose-50/50">
                  <p className="font-semibold text-rose-900">🛠️ Correções e estabilidade</p>
                  <p className="text-rose-800">Correções de bugs e melhorias de performance em várias telas.</p>
                </div>
                <div className="p-3 rounded-lg border border-indigo-100 bg-indigo-50/50">
                  <p className="font-semibold text-indigo-900">📚 Página Ajuda atualizada</p>
                  <p className="text-indigo-800">Conteúdo reformulado com guias e respostas mais rápidas.</p>
                </div>
                <div className="p-3 rounded-lg border border-cyan-100 bg-cyan-50/50">
                  <p className="font-semibold text-cyan-900">💳 PIX automático nas assinaturas</p>
                  <p className="text-cyan-800">Nova forma de pagamento para assinaturas com cobrança automática.</p>
                </div>
                <div className="p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                  <p className="font-semibold text-slate-900">🧾 Notas fiscais aprimoradas</p>
                  <p className="text-slate-800">Melhorias no fluxo e nas telas de emissão e gestão.</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* CHECKBOX E BOTÕES - MAIS VISÍVEIS */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="dont-show-updates"
                checked={dontShowAgain}
                onCheckedChange={(checked) => setDontShowAgain(Boolean(checked))}
              />
              <Label htmlFor="dont-show-updates" className="text-xs text-muted-foreground cursor-pointer">
                Não mostrar novamente
              </Label>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  saveUpdatePreference();
                  setUpdateModalOpen(false);
                  window.open("/changelog.html", "_blank");
                }}
                disabled={checkingUpdates}
                className="w-full sm:w-auto"
              >
                Ver detalhes
              </Button>
              <Button
                onClick={() => {
                  saveUpdatePreference();
                  setUpdateModalOpen(false);
                }}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                disabled={checkingUpdates}
              >
                Quero aproveitar!
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Header */}
      <FadeIn className="bg-white/90 backdrop-blur-lg border-b border-purple-100 sticky top-0 z-30 shadow-lg">
        <div className="p-4 md:p-6">
          {/* Mobile Layout */}
          <div className="md:hidden">
            <div className="flex items-center justify-center mb-4">
              <div className="text-center">
                <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-600 text-xs">Visao geral do seu negocio</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => navigate("/orcamentos/novo")} 
                variant="outline" 
                size="sm"
                className="flex-1 bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="text-xs">Orcamento</span>
              </Button>
              <Button
                onClick={() => navigate("/pedidos/novo")}
                size="sm"
                className="flex-1 animated-gradient-button-purple shadow-lg"
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="text-xs">Pedido</span>
              </Button>
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-600 text-sm mt-0.5">Visao geral do seu negocio</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => navigate("/orcamentos/novo")} 
                variant="outline" 
                size="lg"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Orçamento
              </Button>
              <Button
                onClick={() => navigate("/pedidos/novo")}
                size="lg"
                className="animated-gradient-button-purple shadow-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Pedido
              </Button>
            </div>
          </div>
        </div>

      <div className="p-6 md:p-10 space-y-8 md:space-y-10">
        {/* Banner de Trial - Sticky no topo (abaixo do header) - Só aparece se não for premium */}
        {empresa?.is_premium !== true && (
          <div className="sticky top-[73px] md:top-[89px] z-20 -mx-6 md:-mx-10 px-6 md:px-10 pt-0 pb-4 bg-gradient-to-br from-purple-50 via-pink-50 to-slate-50">
            <TrialBannerSmall />
          </div>
        )}

        {/* Controles do Dashboard */}
        <DashboardControls
          compactMode={compactMode}
          engagementVisible={engagementVisible}
          onCompactChange={setCompactMode}
          onEngagementChange={setEngagementVisible}
        />

        {/* Modal - Explicações das Novidades */}
        <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">✨ Novidades do Ateliê Pro</DialogTitle>
              <DialogDescription>
                Confira as novas funcionalidades e melhorias para deixar seus processos ainda mais completos
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Artes e Logotipos */}
              <div className="border-l-4 border-purple-600 pl-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">🎨 Mais recursos para artes e logotipos</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Agora você pode adicionar múltiplas artes ou logotipos em cada pedido ou orçamento, com informações detalhadas sobre cada uma.
                </p>
                <div className="bg-purple-50 p-3 rounded text-sm space-y-2 text-gray-700">
                  <p><strong>O que incluímos:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Adicionar <strong>múltiplas artes/logotipos</strong> por pedido</li>
                    <li>Campo para informar <strong>onde será aplicada</strong> (peito, costas, ombro, manga, etc)</li>
                    <li><strong>Descrição para cada arte</strong> (cor, tamanho, observações)</li>
                    <li>Visualização com <strong>miniaturas dos arquivos</strong></li>
                    <li>Opção de <strong>abrir em tela cheia</strong> para análise detalhada</li>
                  </ul>
                </div>
              </div>

              {/* Timeline de Produção */}
              <div className="border-l-4 border-blue-600 pl-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">🏭 Nova Timeline de Produção</h3>
                <p className="text-sm text-gray-600 mb-3">
                  A Timeline de Produção ficou muito mais completa e personalizável, permitindo controlar cada etapa do seu processo.
                </p>
                <div className="bg-blue-50 p-3 rounded text-sm space-y-2 text-gray-700">
                  <p><strong>Recursos disponíveis:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Crie suas próprias <strong>etapas customizadas</strong> (até 60)</li>
                    <li><strong>Organize na ordem que preferir</strong> - arraste e solte</li>
                    <li>Personalize cada etapa com <strong>ícones e cores</strong></li>
                    <li>Adicione <strong>observações em cada etapa</strong></li>
                    <li>Registre detalhes e informações importantes durante a produção</li>
                  </ul>
                </div>
              </div>

              {/* Cupom Fiscal */}
              <div className="border-l-4 border-emerald-600 pl-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">🧾 Melhorias na seção Cupom Fiscal</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Novo painel de customização do cupom fiscal com mais opções de personalização e controle.
                </p>
                <div className="bg-emerald-50 p-3 rounded text-sm space-y-2 text-gray-700">
                  <p><strong>Novas funcionalidades:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Escolha entre <strong>manter ou remover avisos</strong> no cupom</li>
                    <li><strong>Controle sobre avisos padrão</strong> (peças promocionais, trocas, etc)</li>
                    <li>Novo campo para <strong>observações personalizadas</strong> da sua empresa</li>
                    <li>Observações aparecem em <strong>cada via do cupom</strong></li>
                    <li>Interface intuitiva com <strong>2 passos:</strong> selecionar via → customizar</li>
                  </ul>
                </div>
              </div>

              {/* Acompanhamento em Tempo Real */}
              <div className="border-l-4 border-pink-600 pl-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">📱 Acompanhamento de Pedido em Tempo Real</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Seus clientes agora podem acompanhar o status do pedido em tempo real através de um link compartilhável, sem precisar entrar no sistema.
                </p>
                <div className="bg-pink-50 p-3 rounded text-sm space-y-2 text-gray-700">
                  <p><strong>Recursos disponíveis:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>Link compartilhável</strong> para cada pedido - envie via WhatsApp ou email</li>
                    <li><strong>Timeline visual</strong> com todas as etapas da produção</li>
                    <li><strong>Barra de progresso</strong> animada mostrando avanço do pedido</li>
                    <li><strong>Informações do cliente:</strong> nome, data de entrega, status atual</li>
                    <li><strong>Listagem de itens</strong> do pedido com valores</li>
                    <li><strong>Botão WhatsApp</strong> direto para tirar dúvidas</li>
                    <li><strong>100% seguro:</strong> clientes veem apenas seus dados públicos</li>
                  </ul>
                </div>
              </div>

              {/* Rodapé */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <p className="text-sm text-gray-700">
                  <strong>🚀 Todas essas melhorias já estão disponíveis!</strong> Comece a usar agora mesmo para deixar seus processos ainda mais profissionais e organizados.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                onClick={() => setShowUpdateModal(false)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
              >
                Entendido! 💜
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Onboarding Checklist - Lazy loaded */}
        <Suspense fallback={null}>
          <OnboardingChecklist />
        </Suspense>

        {/* Card de Atualizações */}
        {showUpdateCard && (
        <Card className="bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 border border-purple-200 shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-2 duration-500">
          <CardHeader className="pb-0 pt-1.5 px-2.5 animated-gradient-header flex flex-row items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-white font-bold text-xs">✨ Novidades</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleExpandUpdateCard}
              className="h-5 w-5 p-0 text-white hover:bg-white/20"
              title={isExpandedUpdateCard ? "Recolher" : "Expandir"}
            >
              {isExpandedUpdateCard ? (
                <EyeOff className="h-2.5 w-2.5" />
              ) : (
                <Eye className="h-2.5 w-2.5" />
              )}
            </Button>
          </CardHeader>
          {isExpandedUpdateCard && (
          <CardContent className="pt-1.5 pb-1.5 px-2.5 space-y-1.5">
            {/* Artes e Logotipos */}
            <div className="flex items-start gap-1.5 p-1.5 rounded-lg bg-white/50 hover:bg-white/80 transition-colors">
              <span className="text-xl flex-shrink-0 mt-0.25">🎨</span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-800 text-[11px] mb-0.5">Artes e logotipos</p>
                <p className="text-[10px] text-gray-600 leading-snug">Múltiplas artes por pedido, localização e miniaturas</p>
              </div>
            </div>

            {/* Timeline de Produção */}
            <div className="flex items-start gap-1.5 p-1.5 rounded-lg bg-white/50 hover:bg-white/80 transition-colors">
              <span className="text-xl flex-shrink-0 mt-0.25">🏭</span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-800 text-[11px] mb-0.5">Timeline de Produção</p>
                <p className="text-[10px] text-gray-600 leading-snug">60 etapas customizadas com ícones, cores e observações</p>
              </div>
            </div>

            {/* Cupom Fiscal */}
            <div className="flex items-start gap-1.5 p-1.5 rounded-lg bg-white/50 hover:bg-white/80 transition-colors">
              <span className="text-xl flex-shrink-0 mt-0.25">🧾</span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-800 text-[11px] mb-0.5">Cupom Fiscal</p>
                <p className="text-[10px] text-gray-600 leading-snug">Controle avisos e adicione observações personalizadas</p>
              </div>
            </div>

            {/* Acompanhamento de Pedido em Tempo Real */}
            <div className="flex items-start gap-1.5 p-1.5 rounded-lg bg-white/50 hover:bg-white/80 transition-colors">
              <span className="text-xl flex-shrink-0 mt-0.25">📱</span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-800 text-[11px] mb-0.5">Acompanhamento em Tempo Real</p>
                <p className="text-[10px] text-gray-600 leading-snug">Clientes acompanham status do pedido via link compartilhável</p>
              </div>
            </div>

            <Button
              onClick={() => setShowUpdateModal(true)}
              className="w-full mt-1 h-5 text-[10px] bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 font-semibold"
            >
              Saiba Mais →
            </Button>
          </CardContent>
          )}
        </Card>
        )}

        {/* In-App Messages e Notificações - Lazy loaded */}
        <Suspense fallback={null}>
          <InAppMessages />
        </Suspense>
        
        {/* Stats Cards */}
        <div className="grid gap-4 md:gap-6 grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
          {isCriticalLoading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              <Card className="bg-gradient-to-br from-white via-purple-50/30 to-white border border-purple-200/40 shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 animate-scale-in">
                <CardHeader className="flex flex-row items-center justify-between pb-3 px-4 md:px-6 pt-4 md:pt-6">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Pedidos em Andamento
                  </CardTitle>
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-3xl bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center shadow-md flex-shrink-0">
                    <Package className="h-6 w-6 md:h-7 md:w-7 text-purple-700" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 md:px-6">
                  <div className="text-3xl md:text-5xl font-bold text-gray-900 mb-2 md:mb-3">{ordersStats.total}</div>
                  <div className="flex items-center gap-2 mt-2 md:mt-3">
                    <TrendingUp className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    <p className="text-xs md:text-sm text-gray-600 font-medium truncate">
                      {ordersStats.inProduction} em produção
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-white via-blue-50/30 to-white border border-blue-200/40 shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 animate-scale-in">
                <CardHeader className="flex flex-row items-center justify-between pb-3 px-4 md:px-6 pt-4 md:pt-6">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Em Produção
                  </CardTitle>
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-3xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-md flex-shrink-0">
                    <Clock className="h-6 w-6 md:h-7 md:w-7 text-blue-700" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 md:px-6">
                  <div className="text-3xl md:text-5xl font-bold text-gray-900 mb-2 md:mb-3">
                    {ordersStats.inProduction}
                  </div>
                  <div className="flex items-center gap-2 mt-2 md:mt-3">
                    <p className="text-xs md:text-sm text-gray-600 font-medium truncate">
                      {ordersStats.urgent.length} com prazo próximo
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-white via-green-50/30 to-white border border-green-200/40 shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 animate-scale-in">
                <CardHeader className="flex flex-row items-center justify-between pb-3 px-4 md:px-6 pt-4 md:pt-6">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Concluídos Hoje
                  </CardTitle>
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-3xl bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center shadow-md flex-shrink-0">
                    <CheckCircle className="h-6 w-6 md:h-7 md:w-7 text-green-700" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 md:px-6">
                  <div className="text-3xl md:text-5xl font-bold text-gray-900 mb-2 md:mb-3">
                    {ordersStats.ready}
                  </div>
                  <div className="flex items-center gap-2 mt-2 md:mt-3">
                    <p className="text-xs md:text-sm text-gray-600 font-medium truncate">
                      {ordersStats.awaitingPickup} aguardando retirada
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-100 via-pink-50 to-white border-2 border-purple-400 shadow-2xl hover:shadow-3xl hover:scale-[1.03] ring-2 ring-purple-200/30 hover:ring-purple-300/50 transition-all duration-300 animate-scale-in">
                <CardHeader className="flex flex-row items-center justify-between pb-3 px-4 md:px-6 pt-4 md:pt-6">
                  <CardTitle className="text-sm font-medium text-purple-700">
                    Receita do Mês
                  </CardTitle>
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-3xl bg-gradient-to-br from-purple-200 to-purple-300 flex items-center justify-center shadow-lg flex-shrink-0">
                    <TrendingUp className="h-6 w-6 md:h-7 md:w-7 text-purple-800" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 md:px-6 pb-4 md:pb-6">
                  <div className="text-3xl md:text-5xl font-bold text-purple-700 mb-2 md:mb-3 break-words">
                    {formatCurrency(ordersStats.totalRevenue)}
                  </div>
                  <div className="flex items-center gap-2 mt-2 md:mt-3">
                    <TrendingUp className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    <p className="text-xs md:text-sm text-purple-700 font-semibold truncate">
                      {receitasLoading ? (
                        <span className="text-gray-400">Carregando...</span>
                      ) : (
                        formatCurrency(ordersStats.receivedRevenue) + " recebido"
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>

            </>
          )}
        </div>

        {/* Card de Saúde Financeira - Premium */}
        <Card className="bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 w-full">
          <CardContent className="px-3 md:px-4 py-2 md:py-3">
            {console.log('🎨 Renderizando Card de Saúde Financeira - showFinancialValues:', showFinancialValues)}
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-700">Saúde Financeira</p>
                  <div className={`flex items-center gap-2 mt-2 px-3 py-1.5 rounded-md text-xs font-semibold w-fit ${
                    healthMetrics.saudavel
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {healthMetrics.saudavel ? 'Saudável' : 'Atenção'}
                  </div>
                </div>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    console.log('🖱️ Mouse down detectado!', e);
                  }}
                  onClick={(e) => {
                    console.log('👁️ Clique detectado! e:', e);
                    console.log('👁️ Mudando de', showFinancialValues, 'para', !showFinancialValues);
                    e.preventDefault();
                    e.stopPropagation();
                    setShowFinancialValues(!showFinancialValues);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors z-50 relative"
                  title={showFinancialValues ? 'Ofuscar valores' : 'Mostrar valores'}
                >
                  {showFinancialValues ? (
                    <Eye className="w-5 h-5 text-gray-600" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>

              <div className="grid grid-cols-2 md:flex md:flex-row gap-4 md:gap-6">
                {/* Saldo Atual */}
                <div
                  onClick={() => navigate('/fluxo-caixa')}
                  onMouseEnter={() => setHoverCard('saldo')}
                  onMouseLeave={() => setHoverCard(null)}
                  className={`cursor-pointer transition-all duration-200 p-1 rounded-md ${
                    hoverCard === 'saldo' ? 'bg-gray-100' : ''
                  }`}
                >
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Saldo</p>
                  <p className={`text-base md:text-lg font-semibold mt-0.5 ${
                    healthMetrics.saldo >= 0 ? 'text-gray-700' : 'text-red-600'
                  }`}>
                    {showFinancialValues ? formatCurrency(healthMetrics.saldo) : '••••'}
                  </p>
                </div>

                {/* A Receber */}
                <div
                  onClick={() => navigate('/financeiro')}
                  onMouseEnter={() => setHoverCard('receber')}
                  onMouseLeave={() => setHoverCard(null)}
                  className={`cursor-pointer transition-all duration-200 p-1 rounded-md ${
                    hoverCard === 'receber' ? 'bg-gray-100' : ''
                  }`}
                >
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Receber</p>
                  <p className="text-lg md:text-xl font-semibold mt-1 text-gray-700">
                    {showFinancialValues ? formatCurrency(healthMetrics.aReceber) : '••••'}
                  </p>
                </div>

                {/* A Pagar */}
                <div
                  onClick={() => navigate('/fluxo-caixa')}
                  onMouseEnter={() => setHoverCard('pagar')}
                  onMouseLeave={() => setHoverCard(null)}
                  className={`cursor-pointer transition-all duration-200 p-1 rounded-md ${
                    hoverCard === 'pagar' ? 'bg-gray-100' : ''
                  }`}
                >
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Pagar</p>
                  <p className="text-lg md:text-xl font-semibold mt-1 text-gray-700">
                    {showFinancialValues ? formatCurrency(healthMetrics.aPagar) : '••••'}
                  </p>
                </div>

                {/* Atrasados */}
                <div
                  onClick={() => navigate('/financeiro')}
                  onMouseEnter={() => setHoverCard('atrasado')}
                  onMouseLeave={() => setHoverCard(null)}
                  className={`cursor-pointer transition-all duration-200 p-1 rounded-md ${
                    hoverCard === 'atrasado' ? 'bg-gray-100' : ''
                  }`}
                >
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Atrasado</p>
                  <p className={`text-lg md:text-xl font-semibold mt-1 ${
                    healthMetrics.atrasados > 0 ? 'text-red-600' : 'text-gray-700'
                  }`}>
                    {showFinancialValues ? formatCurrency(healthMetrics.atrasados) : '••••'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Seções de Engajamento - Controladas pelo toggle (entre Stats Cards e Ações Rápidas) - Lazy loaded */}
        {engagementVisible && (
          <Suspense fallback={<div className="h-32 bg-gray-100 rounded-lg animate-pulse" />}>
            <div className={compactMode ? "space-y-4 md:space-y-6" : "space-y-8 md:space-y-10"}>
              {/* Dashboard de Valor (ROI) */}
              <ValueDashboard />
              
              {/* Badges e Achievements */}
              <AchievementsBadges />
              
              {/* Programa de Referência */}
              <ReferralProgram />
            </div>
          </Suspense>
        )}

        {/* Acoes Rapidass */}
        <FadeIn>
          <Card className="bg-white border border-gray-200/50 shadow-md">
            <CardHeader className="px-6 pt-6">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageCircle className="w-6 h-6 text-purple-600" />
                Ações Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              <MobileGrid cols={2} className="gap-5">
                <MobileCard 
                  onClick={() => navigate("/calculadora")}
                  interactive
                  className="h-28 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-blue-100 via-blue-50 to-blue-100 border-2 border-blue-300/40 hover:border-blue-400 hover:shadow-xl hover:scale-105 rounded-3xl transition-all animate-fade-in-up"
                >
                  <div className="w-12 h-12 rounded-3xl bg-blue-200 flex items-center justify-center">
                    <Calculator className="w-7 h-7 text-blue-700" />
                  </div>
                  <span className="text-blue-800 font-bold text-sm text-center">Calculadora de Preços</span>
                </MobileCard>
                
                <MobileCard 
                  onClick={() => navigate("/catalogo")}
                  interactive
                  className="h-28 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-green-100 via-green-50 to-green-100 border-2 border-green-300/40 hover:border-green-400 hover:shadow-xl hover:scale-105 rounded-3xl transition-all animate-fade-in-up"
                >
                  <div className="w-12 h-12 rounded-3xl bg-green-200 flex items-center justify-center">
                    <BookOpen className="w-7 h-7 text-green-700" />
                  </div>
                  <span className="text-green-800 font-bold text-sm text-center">Catálogo de Produtos</span>
                </MobileCard>
                
                <MobileCard 
                  onClick={async () => {
                    // Usar template personalizado se existir, senão usar padrão
                    let message = whatsappTemplate?.message_text;
                    
                    if (!message) {
                      // Template padrão
                      message = `Olá!

Sou do ${empresa?.nome || 'Atelie'} e gostaria de saber como posso ajudar você hoje!

*NOSSOS SERVIÇOS:*
• Bordados computadorizados
• Uniformes personalizados  
• Camisetas estampadas
• Produtos personalizados

*Entre em contato conosco para um orçamento personalizado!*

_${empresa?.nome || 'Atelie'} - Qualidade e criatividade em cada peça_`;
                    }
                    
                    // Buscar configurações para número do WhatsApp
                    const { getWhatsAppSettings, generateWhatsAppUrl } = await import("@/utils/whatsappTemplates");
                    const settings = empresa?.id ? await getWhatsAppSettings(empresa.id) : null;
                    
                    const whatsappUrl = generateWhatsAppUrl(message, settings?.whatsapp_number);
                    window.open(whatsappUrl, '_blank');
                  }}
                  interactive
                  className="h-28 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-emerald-100 via-emerald-50 to-emerald-100 border-2 border-emerald-300/40 hover:border-emerald-400 hover:shadow-xl hover:scale-105 rounded-3xl transition-all animate-fade-in-up"
                >
                  <div className="w-12 h-12 rounded-3xl bg-emerald-200 flex items-center justify-center">
                    <MessageCircle className="w-7 h-7 text-emerald-700" />
                  </div>
                  <span className="text-emerald-800 font-bold text-sm text-center">Template WhatsApp</span>
                </MobileCard>
                
                <MobileCard 
                  onClick={() => navigate("/relatorios")}
                  interactive
                  className="h-28 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-purple-100 via-purple-50 to-purple-100 border-2 border-purple-300/40 hover:border-purple-400 hover:shadow-xl hover:scale-105 rounded-3xl transition-all animate-fade-in-up"
                >
                  <div className="w-12 h-12 rounded-3xl bg-purple-200 flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-purple-700" />
                  </div>
                  <span className="text-purple-800 font-bold text-sm text-center">Relatórios</span>
                </MobileCard>
              </MobileGrid>
          </CardContent>
        </Card>
        </FadeIn>

        {/* Aviso de Vencimento de Pagamento */}
        <PaymentExpirationWarning />

        {/* Centro de Alertas Inteligentes */}
        <FadeIn>
          <Card className="bg-gradient-to-br from-white via-purple-50/20 to-white border border-purple-200/50 shadow-xl">
          <CardHeader className="border-b border-purple-100 px-4 md:px-6 pt-4 md:pt-6 pb-4 md:pb-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
              <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center shadow-md flex-shrink-0">
                  <Bell className="w-6 h-6 md:w-7 md:h-7 text-purple-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg md:text-2xl font-bold text-gray-900">
                    Centro de Alertas Inteligentes
                  </CardTitle>
                  <p className="text-xs md:text-sm text-gray-600 mt-0.5">Acompanhe o status do seu negócio</p>
                </div>
                {intelligentAlerts.length > 0 && (
                  <Badge variant="destructive" className="text-xs font-bold px-2 md:px-3 py-1 flex-shrink-0">
                    {intelligentAlerts.length}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => navigate("/agenda")}
                  className="text-xs md:text-sm text-blue-700 border-blue-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:border-blue-400 font-semibold flex-1 md:flex-none"
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  <span className="hidden md:inline">Ver Agenda</span>
                  <span className="md:hidden">Agenda</span>
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => navigate("/financeiro")}
                  className="text-xs md:text-sm text-green-700 border-green-300 hover:bg-gradient-to-r hover:from-green-50 hover:to-green-100 hover:border-green-400 font-semibold flex-1 md:flex-none"
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  <span className="hidden md:inline">Ver Financeiro</span>
                  <span className="md:hidden">Financeiro</span>
              </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 md:p-6">
            {intelligentAlerts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center mx-auto mb-5 shadow-lg">
                  <CheckCircle className="w-12 h-12 text-green-700" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Tudo em ordem! 🎉</h3>
                <p className="text-gray-600 text-lg">Nenhum alerta crítico no momento</p>
              </div>
            ) : (
              <div className="space-y-5">
                {intelligentAlerts.map((alert) => {
                  const IconComponent = alert.icon;
                  
                  return (
                    <div
                      key={alert.id}
                      className={`p-4 md:p-6 rounded-2xl border-2 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer ${
                        alert.color === 'red' ? 'bg-gradient-to-br from-red-50 to-red-100/50 border-red-300 hover:border-red-400' :
                        alert.color === 'orange' ? 'bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-300 hover:border-orange-400' :
                        alert.color === 'blue' ? 'bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-300 hover:border-blue-400' :
                        'bg-gradient-to-br from-green-50 to-green-100/50 border-green-300 hover:border-green-400'
                      }`}
                      onClick={alert.action}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-3 md:gap-5">
                          <div className={`w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0 ${
                            alert.color === 'red' ? 'bg-gradient-to-br from-red-100 to-red-200' :
                            alert.color === 'orange' ? 'bg-gradient-to-br from-orange-100 to-orange-200' :
                            alert.color === 'blue' ? 'bg-gradient-to-br from-blue-100 to-blue-200' :
                            'bg-gradient-to-br from-green-100 to-green-200'
                          }`}>
                            <IconComponent className={`w-6 h-6 md:w-8 md:h-8 ${
                              alert.color === 'red' ? 'text-red-700' :
                              alert.color === 'orange' ? 'text-orange-700' :
                              alert.color === 'blue' ? 'text-blue-700' :
                              'text-green-700'
                            }`} />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="font-bold text-base md:text-lg text-gray-900">{alert.title}</h3>
                              <Badge 
                                variant="outline" 
                                className={`font-bold text-xs flex-shrink-0 ${
                                  alert.priority === 'high' ? 'bg-red-100 text-red-800 border-red-400' :
                                  alert.priority === 'medium' ? 'bg-orange-100 text-orange-800 border-orange-400' :
                                  'bg-blue-100 text-blue-800 border-blue-400'
                                }`}
                              >
                                {alert.priority === 'high' ? 'Alta' : alert.priority === 'medium' ? 'Media' : 'Baixa'}
                              </Badge>
                            </div>
                            <p className={`text-sm font-medium truncate md:truncate-none ${
                              alert.color === 'red' ? 'text-red-700' :
                              alert.color === 'orange' ? 'text-orange-700' :
                              alert.color === 'blue' ? 'text-blue-700' :
                              'text-green-700'
                            }`}>{alert.message}</p>
                          </div>
                        </div>

                        <div className="flex justify-end md:block">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              alert.action();
                            }}
                            className="w-full md:w-auto text-green-700 border-green-400 hover:bg-gradient-to-r hover:from-green-100 hover:to-green-50 hover:border-green-500 font-semibold shadow-sm hover:shadow-md transition-all"
                          >
                            <MessageCircle className="w-4 h-4 mr-1" />
                            WhatsApp
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        </FadeIn>
      </div>
      </FadeIn>
    </PageTransition>
  );
}
